import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL, BRAND } from "@/config";
import { Loader2 } from "lucide-react";

export const AuthDialog = () => {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      setAuthOpen(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-[#f3f4f6] sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <img src={LOGO_URL} alt={BRAND} className="h-8 w-8 object-contain" />
            <span className="font-heading font-extrabold text-lg tracking-tight">{BRAND}</span>
          </div>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#9ca3af]">
            {mode === "login" ? "Sign in to access your library and buy beats." : "Join to purchase and download premium beats."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          {mode === "register" && (
            <div>
              <Label className="text-[#9ca3af] text-sm">Name</Label>
              <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                className="bg-[#121212] border-[#2a2a2a] mt-1 focus-visible:ring-[#e2ff31]" placeholder="Your name" />
            </div>
          )}
          <div>
            <Label className="text-[#9ca3af] text-sm">Email</Label>
            <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="bg-[#121212] border-[#2a2a2a] mt-1 focus-visible:ring-[#e2ff31]" placeholder="you@email.com" />
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">Password</Label>
            <Input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="bg-[#121212] border-[#2a2a2a] mt-1 focus-visible:ring-[#e2ff31]" placeholder="••••••••" />
          </div>

          {error && <p data-testid="auth-error" className="text-sm text-[#ef4444]">{error}</p>}

          <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
            className="w-full rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <p className="text-center text-sm text-[#9ca3af]">
          {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          <button data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-[#e2ff31] hover:underline font-medium">
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
